import { useMutation, useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeftRight,
  BookText,
  ChartCandlestick,
  CircleCheck,
  Download,
  FileSpreadsheet,
  Fingerprint,
  Gift,
  Globe,
  LogIn,
  RefreshCw,
  Scale,
  ServerCrash,
  Share2,
  ShieldCheck,
  Coins,
  Copy,
  ArrowDownToLine,
  ArrowUpFromLine,
  Users,
} from 'lucide-react'

import { downloadReport, getDuplicatesReport, getReportSummary, type DuplicateGroup, type DuplicateKind, type ReportKind, type ReportSummary } from '../api/admin'
import { PageHeader } from '../components/layout'
import {
  Badge,
  Button,
  Card,
  CountUp,
  EmptyState,
  IdChip,
  Input,
  Progress,
  SegmentedControl,
  Skeleton,
  Spinner,
  StatusPill,
  Table,
  TableSkeleton,
  cx,
  describeError,
  formatNumber,
  money,
  tableClass,
  toNumber,
  useToast,
  when,
  type TabOption,
} from '../components/ui/Ui'
import { useCopyEnabled } from '../lib/settings'
import styles from './Reports.module.css'

/**
 * The books for a period: sums over rows that already exist, and the same
 * rows as CSV for whoever keeps the accounts. Nothing here is typed in.
 */

const DUPLICATE_KIND: Record<DuplicateKind, string> = { signup_device: 'Устройство при регистрации', signup_ip: 'Адрес при регистрации', login_ip: 'Адрес входа, 30 дней' }
const DUPLICATE_ICON: Record<DuplicateKind, ReactNode> = { signup_device: <Fingerprint />, signup_ip: <Globe />, login_ip: <LogIn /> }

const KINDS: ReadonlyArray<{ kind: ReportKind; label: string; hint: string; icon: ReactNode }> = [
  { kind: 'trades', label: 'Сделки', hint: 'каждое исполнение: рынок, субсчёт, цена, объём, комиссия', icon: <ChartCandlestick /> },
  { kind: 'ledger', label: 'Леджер', hint: 'каждая проводка с остатком после неё', icon: <BookText /> },
  { kind: 'deposits', label: 'Депозиты', hint: 'ончейн-поступления со статусом', icon: <ArrowDownToLine /> },
  { kind: 'withdrawals', label: 'Выводы', hint: 'заявки, решения, хеши', icon: <ArrowUpFromLine /> },
  { kind: 'rewards', label: 'Партнёрка', hint: 'начисления партнёрам и бонусы', icon: <Gift /> },
  { kind: 'insurance', label: 'Страховой фонд', hint: 'приход с ликвидаций и покрытые недостачи', icon: <ShieldCheck /> },
]

function dayAgo(days: number): string {
  const date = new Date()
  date.setUTCDate(date.getUTCDate() - days)
  return date.toISOString().slice(0, 10)
}

/** «1 день», «3 дня», «31 день», «12 дней». */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

/** «30.08.2026» from «2026-08-30». */
function ruDate(iso: string): string {
  const [y, m, d] = iso.split('-')
  return y && m && d ? `${d}.${m}.${y}` : iso
}

/** Days in an inclusive range of ISO dates. */
function daysIn(from: string, to: string): number {
  const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)
  return Number.isFinite(ms) ? Math.round(ms / 86_400_000) + 1 : 0
}

type Preset = '7' | '30' | '90' | 'custom'
const PRESETS: readonly TabOption<Preset>[] = [
  { value: '7', label: '7 дней' },
  { value: '30', label: '30 дней' },
  { value: '90', label: '90 дней' },
  { value: 'custom', label: 'Свой период' },
]

function presetOf(from: string, to: string): Preset {
  if (to !== dayAgo(0)) return 'custom'
  for (const days of [7, 30, 90] as const) if (from === dayAgo(days)) return String(days) as Preset
  return 'custom'
}

/* ------------------------------------------------------------------------ */
/* summary pieces (local: the kit's KeyValue left-aligns values)            */
/* ------------------------------------------------------------------------ */

/** The figure a card leads with: caption, mono count-up, unit. */
function Lead({ label, value, decimals, unit, tone, title, loading }: { label: ReactNode; value: string | number | undefined; decimals?: number; unit?: string; tone?: 'up' | 'down'; title?: string; loading: boolean }): JSX.Element {
  return (
    <div className={styles.lead}>
      <span className={styles.leadLabel}>{label}</span>
      {loading ? (
        <Skeleton width={120} height={28} />
      ) : (
        <span className={cx(styles.leadFigure, tone === 'down' ? styles.down : tone === 'up' ? styles.up : '')} title={title}>
          <CountUp value={value} decimals={decimals} />
          {unit ? <span className={styles.leadUnit}>{unit}</span> : null}
        </span>
      )}
    </div>
  )
}

interface Metric {
  label: ReactNode
  value: ReactNode
  /** A quiet line under the label. */
  sub?: ReactNode
  tone?: 'warning' | 'down' | 'up'
  /** Nested under the previous row (a per-asset breakdown). */
  nested?: boolean
}

/** Label on the left, mono figure on the right, hairlines between rows. */
function Metrics({ rows, loading }: { rows: ReadonlyArray<Metric | null | false>; loading: boolean }): JSX.Element {
  const shown = rows.filter((row): row is Metric => Boolean(row))
  return (
    <dl className={styles.metrics}>
      {shown.map((row, i) => (
        <div key={i} className={cx(styles.metric, row.nested ? styles.metricNested : '')}>
          <dt className={styles.metricLabel}>
            <span className={styles.metricName}>{row.label}</span>
            {row.sub ? <span className={styles.metricSub}>{row.sub}</span> : null}
          </dt>
          <dd className={cx(styles.metricValue, row.tone ? styles[row.tone] : '')}>{loading ? <Skeleton width={64} height={12} /> : row.value}</dd>
        </div>
      ))}
    </dl>
  )
}

function signTone(value: string | undefined): 'up' | 'down' | undefined {
  const n = toNumber(value)
  if (n === null || n === 0) return undefined
  return n > 0 ? 'up' : 'down'
}

/** A share of a whole as a thin bar with its percentage (real counts only). */
function Share({ part, whole, label }: { part: number; whole: number; label: string }): JSX.Element | null {
  if (whole <= 0) return null
  const pct = Math.round((part / whole) * 100)
  return (
    <span className={styles.share}>
      <Progress value={part} max={whole} label={label} className={styles.shareBar} />
      <span className={styles.sharePct}>{pct} %</span>
    </span>
  )
}

/* ------------------------------------------------------------------------ */
/* duplicates                                                               */
/* ------------------------------------------------------------------------ */

const MEMBERS_SHOWN = 3

function DuplicateRow({ group }: { group: DuplicateGroup }): JSX.Element {
  const [expanded, setExpanded] = useState(false)
  const members = expanded ? group.members : group.members.slice(0, MEMBERS_SHOWN)
  const hidden = group.members.length - MEMBERS_SHOWN
  return (
    <tr>
      <td className={styles.kindCell}>
        <span className={styles.kind}>
          <span className={styles.kindIcon}>{DUPLICATE_ICON[group.kind]}</span>
          {DUPLICATE_KIND[group.kind]}
        </span>
      </td>
      <td className={styles.keyCell}>
        {/* The server already shortens device fingerprints («a765248b…»): nothing whole to copy then. */}
        <IdChip value={group.key} full copy={!group.key.endsWith('…')} label="значение" />
      </td>
      <td className={tableClass.wrap}>
        <div className={styles.members}>
          <span className={styles.memberCount}>
            {group.members.length} {plural(group.members.length, 'аккаунт', 'аккаунта', 'аккаунтов')}
          </span>
          <ul className={styles.memberList}>
            {members.map((member) => (
              <li key={member.userId} className={styles.member}>
                <Link to={`/users/${member.userId}`} className={styles.memberEmail}>
                  {member.email}
                </Link>
                <StatusPill status={member.status} size="sm" />
                <span className={styles.memberWhen}>{when(member.createdAt)}</span>
              </li>
            ))}
          </ul>
          {hidden > 0 ? (
            <button type="button" className={styles.more} aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>
              {expanded ? 'Свернуть' : `Показать ещё ${hidden}`}
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  )
}

/* ------------------------------------------------------------------------ */
/* screen                                                                   */
/* ------------------------------------------------------------------------ */

export default function Reports(): JSX.Element {
  const toast = useToast()
  // Copy trading is off since 28 September; its panel returns with `copy.enabled`.
  const copyEnabled = useCopyEnabled()
  const [from, setFrom] = useState(dayAgo(30))
  const [to, setTo] = useState(dayAgo(0))
  const [applied, setApplied] = useState({ from: dayAgo(30), to: dayAgo(0) })

  const summary = useQuery({ queryKey: ['report-summary', applied], queryFn: () => getReportSummary(applied.from, applied.to) })
  const duplicates = useQuery({ queryKey: ['report-duplicates'], queryFn: getDuplicatesReport, staleTime: 60_000 })

  const download = useMutation({
    mutationFn: async (kind: ReportKind) => {
      const file = await downloadReport(kind, applied.from, applied.to)
      const url = URL.createObjectURL(file.blob)
      const link = document.createElement('a')
      link.href = url
      link.download = file.filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      return file
    },
    onSuccess: (file) => toast(`${file.filename}: ${file.rows} строк`),
    onError: (error) => toast(describeError(error), true),
  })

  const s: ReportSummary | undefined = summary.data
  const loading = summary.isLoading
  const invalid = from === '' || to === '' || from > to
  const dirty = from !== applied.from || to !== applied.to
  const preset = presetOf(from, to)
  const days = daysIn(applied.from, applied.to)
  const period = `${ruDate(applied.from)} — ${ruDate(applied.to)}`

  const choosePreset = (next: Preset): void => {
    if (next === 'custom') {
      document.getElementById('report-from')?.focus()
      return
    }
    const range = { from: dayAgo(Number(next)), to: dayAgo(0) }
    setFrom(range.from)
    setTo(range.to)
    setApplied(range)
  }

  const groups = duplicates.data?.groups ?? []

  return (
    <>
      <PageHeader
        title="Отчёты"
        description="Суммы за период по уже существующим записям и те же строки в CSV для бухгалтерии."
        meta={
          <>
            <Badge tone="neutral" size="sm" mono>
              {period}
            </Badge>
            <span className={styles.metaText}>
              {days} {plural(days, 'день', 'дня', 'дней')} · даты в UTC, конец периода включительно
            </span>
          </>
        }
      />

      <div className={styles.page}>
        <form
          className={styles.filters}
          onSubmit={(event) => {
            event.preventDefault()
            if (!invalid) setApplied({ from, to })
          }}
        >
          <SegmentedControl value={preset} options={PRESETS} onChange={choosePreset} size="sm" ariaLabel="Период" />
          <span className={styles.filterDivider} aria-hidden="true" />
          <div className={styles.range}>
            <label className={styles.rangeLabel} htmlFor="report-from">
              С
            </label>
            <Input id="report-from" type="date" compact value={from} max={to || undefined} invalid={from !== '' && to !== '' && from > to} onChange={(event) => setFrom(event.target.value)} className={styles.date} />
            <label className={styles.rangeLabel} htmlFor="report-to">
              по
            </label>
            <Input id="report-to" type="date" compact value={to} min={from || undefined} invalid={from !== '' && to !== '' && from > to} onChange={(event) => setTo(event.target.value)} className={styles.date} />
          </div>
          <Button type="submit" variant={dirty ? 'primary' : 'secondary'} size="sm" disabled={invalid}>
            Показать
          </Button>
          {from !== '' && to !== '' && from > to ? (
            <span className={styles.filterError} role="alert">
              Начало периода позже конца
            </span>
          ) : dirty && !invalid ? (
            <span className={styles.filterHint}>Нажмите «Показать», чтобы пересчитать</span>
          ) : null}
        </form>

        {summary.isError ? (
          <Card>
            <EmptyState
              tone="error"
              icon={<ServerCrash />}
              title="Сводка за период не загрузилась"
              description={describeError(summary.error)}
              action={
                <Button size="sm" variant="secondary" icon={<RefreshCw />} loading={summary.isFetching} onClick={() => void summary.refetch()}>
                  Повторить
                </Button>
              }
            />
          </Card>
        ) : (
          <div className={styles.summaryWrap}>
            <div className={cx(styles.summary, 'stagger')} aria-busy={loading || undefined}>
              <Card className={styles.trading} icon={<ChartCandlestick />} title="Торговля">
                <Lead label="Оборот, USDT" value={s?.trading.turnoverUsdt} decimals={2} title={s ? money(s.trading.turnoverUsdt) : undefined} loading={loading} />
                <Metrics
                  loading={loading}
                  rows={[
                    { label: 'Сделок', value: s ? formatNumber(s.trading.trades) : '—' },
                    { label: 'Торговавших', value: s ? formatNumber(s.trading.traders) : '—' },
                    { label: 'Комиссии удержано, USDT', value: s ? money(s.trading.feesUsdt, 4) : '—' },
                  ]}
                />
              </Card>

              <Card className={styles.money} icon={<ArrowLeftRight />} title="Депозиты и выводы">
                <div className={styles.leadPair}>
                  <Lead label="Депозитов зачислено" value={s?.deposits.count} loading={loading} />
                  <Lead label="Выводов отправлено" value={s?.withdrawals.sent} loading={loading} />
                </div>
                <Metrics
                  loading={loading}
                  rows={[
                    ...(s?.deposits.credited ?? []).map(
                      (row): Metric => ({
                        label: (
                          <>
                            Зачислено <span className={styles.asset}>{row.assetSymbol}</span> <span className={styles.network}>{row.networkCode}</span>
                          </>
                        ),
                        value: money(row.amount, 4),
                      }),
                    ),
                    { label: 'Заявок на вывод', value: s ? formatNumber(s.withdrawals.requested) : '—' },
                    ...(s?.withdrawals.sentAmount ?? []).map(
                      (row): Metric => ({
                        label: (
                          <>
                            Отправлено <span className={styles.asset}>{row.assetSymbol}</span> <span className={styles.network}>{row.networkCode}</span>
                          </>
                        ),
                        sub: `сеть ${money(row.fee, 4)} · платформа ${money(row.platformFee, 4)}`,
                        value: money(row.amount, 4),
                      }),
                    ),
                    {
                      label: 'Ждут решения сейчас',
                      value: s ? formatNumber(s.withdrawals.pending) : '—',
                      tone: s && s.withdrawals.pending > 0 ? 'warning' : undefined,
                    },
                  ]}
                />
              </Card>

              <Card className={styles.partners} icon={<Share2 />} title="Партнёрская программа">
                <Lead label="Выплачено за период, USDT" value={s?.partners.rewardsPaidUsdt} decimals={2} title={s ? money(s.partners.rewardsPaidUsdt) : undefined} loading={loading} />
                <Metrics
                  loading={loading}
                  rows={[
                    { label: 'Квалификаций', value: s ? formatNumber(s.partners.qualifications) : '—' },
                    { label: 'Обязательства на проверке, USDT', value: s ? money(s.partners.rewardsPendingUsdt) : '—' },
                    { label: 'Обязательства одобрены, USDT', value: s ? money(s.partners.rewardsApprovedUsdt) : '—' },
                    { label: 'Промо разблокировано, USDT', value: s ? money(s.partners.promoUnlockedUsdt) : '—' },
                    { label: 'Промо заморожено сейчас, USDT', value: s ? money(s.partners.promoLockedUsdt) : '—' },
                  ]}
                />
              </Card>

              <Card className={styles.futures} icon={<Scale />} title="Фьючерсы">
                <Lead
                  label="Реализованный результат клиентов, USDT"
                  value={s?.futures.realizedPnlUsdt}
                  decimals={2}
                  tone={signTone(s?.futures.realizedPnlUsdt)}
                  title={s ? money(s.futures.realizedPnlUsdt) : undefined}
                  loading={loading}
                />
                <Metrics
                  loading={loading}
                  rows={[
                    { label: 'Позиций закрыто', value: s ? formatNumber(s.futures.positionsClosed) : '—' },
                    { label: 'Ликвидаций', value: s ? formatNumber(s.futures.liquidations) : '—' },
                    { label: 'Фандинг (сумма), USDT', value: s ? money(s.futures.fundingUsdt, 4) : '—' },
                    { label: 'Страховой фонд, нетто, USDT', value: s ? money(s.futures.insuranceNetUsdt, 4) : '—' },
                  ]}
                />
              </Card>

              <Card className={styles.users} icon={<Users />} title="Пользователи">
                <Lead label="Зарегистрировалось" value={s?.users.registered} loading={loading} />
                <Metrics
                  loading={loading}
                  rows={[
                    {
                      label: 'Прошло верификацию',
                      sub: s ? <Share part={s.users.verified} whole={s.users.registered} label="Доля прошедших верификацию" /> : undefined,
                      value: s ? formatNumber(s.users.verified) : '—',
                    },
                    {
                      label: 'Торговало',
                      sub: s ? <Share part={s.users.activeTraders} whole={s.users.registered} label="Доля торговавших" /> : undefined,
                      value: s ? formatNumber(s.users.activeTraders) : '—',
                    },
                  ]}
                />
              </Card>

              {copyEnabled ? (
                <Card className={styles.copy} icon={<Coins />} title="Копи-трейдинг">
                  <Lead label="Под управлением, USDT" value={s?.copy.aumUsdt} decimals={2} loading={loading} />
                  <Metrics
                    loading={loading}
                    rows={[
                      { label: 'Подписок начато', value: s ? formatNumber(s.copy.subscriptionsStarted) : '—' },
                      { label: 'Остановлено', value: s ? formatNumber(s.copy.subscriptionsStopped) : '—' },
                      { label: 'Активных сейчас', value: s ? formatNumber(s.copy.activeNow) : '—' },
                    ]}
                  />
                </Card>
              ) : null}
            </div>
          </div>
        )}

        <Card
          icon={<FileSpreadsheet />}
          title="Выгрузка CSV"
          description={`За ${period}, до 50 000 строк на файл, время в UTC. Файл открывается в любой таблице.`}
        >
          <div className={cx(styles.exports, 'stagger')}>
            {KINDS.map((item) => {
              const busy = download.isPending && download.variables === item.kind
              return (
                <button
                  key={item.kind}
                  type="button"
                  className={styles.exportTile}
                  onClick={() => download.mutate(item.kind)}
                  disabled={busy}
                  aria-busy={busy || undefined}
                  title={item.hint}
                >
                  <span className={styles.exportIcon}>{item.icon}</span>
                  <span className={styles.exportText}>
                    <span className={styles.exportLabel}>{item.label}</span>
                    <span className={styles.exportHint}>{item.hint}</span>
                  </span>
                  <span className={styles.exportGo} aria-hidden="true">
                    {busy ? <Spinner size={14} /> : <Download />}
                  </span>
                </button>
              )
            })}
          </div>
        </Card>

        <Card
          icon={<Copy />}
          title="Дубликаты аккаунтов"
          description="Аккаунты с общим устройством или адресом при регистрации и общим адресом входа за 30 дней. Общий домашний Wi-Fi выглядит так же, как мультиаккаунт: это очередь на просмотр, а не вердикт. Не зависит от периода."
          actions={
            duplicates.data ? (
              <span className={styles.dupMeta}>
                {groups.length > 0 ? (
                  <Badge tone="warning" size="sm">
                    {groups.length} {plural(groups.length, 'совпадение', 'совпадения', 'совпадений')}
                  </Badge>
                ) : null}
                <span className={styles.metaText}>на {when(duplicates.data.generatedAt)}</span>
              </span>
            ) : null
          }
          flush
        >
          {duplicates.isLoading ? (
            <TableSkeleton rows={4} headers={['Признак', 'Значение', 'Аккаунты']} />
          ) : duplicates.isError ? (
            <EmptyState
              compact
              tone="error"
              icon={<ServerCrash />}
              title="Отчёт о дубликатах не загрузился"
              description={describeError(duplicates.error)}
              action={
                <Button size="sm" variant="secondary" icon={<RefreshCw />} loading={duplicates.isFetching} onClick={() => void duplicates.refetch()}>
                  Повторить
                </Button>
              }
            />
          ) : groups.length === 0 ? (
            <EmptyState compact tone="success" icon={<CircleCheck />} title="Совпадений нет" description="Ни устройство, ни адрес не повторяются у разных аккаунтов." />
          ) : (
            <Table ariaLabel="Дубликаты аккаунтов" className={styles.dupTable}>
              <thead>
                <tr>
                  <th>Признак</th>
                  <th>Значение</th>
                  <th>Аккаунты</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <DuplicateRow key={`${group.kind}:${group.key}`} group={group} />
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    </>
  )
}
